import type {
  AudioUploadFile,
  CreationInfo,
  CreationWithTask,
  ElementType,
  FormAudioPrompt,
  FormImagePrompt,
  FormSubtitlePrompt,
  ImagePrompt,
  ImageUploadFile,
  SubtitleUploadFile,
  SupportedLanguage,
  TaskInfo,
  TaskInputPrompts,
  TaskSettings,
  ToolFormParams,
  ToolTaskFormVideoPrompt,
  ToolTaskType,
  VideoPrompt,
  VideoUploadFile,
} from '@/app/types';
import { getResourceUrl } from '@/app/utils';
import { buildOneClickMvRecreatePatch, sanitizeToolRecreatePatchReferences } from '@/app/utils/client';
import type { FormPromptListHelpers, FormPromptListItem } from '../types';

type LocalizedExample = {
  images: { name: string; url: string }[];
  videos?: { name: string; url: string; coverUrl: string; duration: number }[];
  text: string;
  settings?: TaskSettings;
  thumbnail?: string;
};

export type CompactExample = Partial<Record<SupportedLanguage, LocalizedExample>>;

export const expandExamples = (examples: CompactExample[], locale: SupportedLanguage) => {
  return examples.map((example) => {
    const localizedExample = example[locale] ?? example.en;

    if (!localizedExample) {
      return {
        images: [],
        videos: [],
        text: '',
      };
    }

    return {
      ...localizedExample,
      images: (localizedExample.images || []).map((image) => {
        const imageUrl = image.url;
        return {
          sourceImage: imageUrl,
          sourceImagePreview: imageUrl,
          selectedImage: imageUrl,
          selectedImagePreview: imageUrl,
        };
      }),
      videos: (localizedExample.videos || []).map((video) => ({
        video: video.url,
        videoPreview: video.url,
        coverImage: video.coverUrl,
        coverImagePreview: video.coverUrl,
        duration: video.duration,
      })),
    };
  });
};

const getSelectedImageRange = (prompt: TaskInputPrompts): ImagePrompt['selectedImageRange'] => {
  if (!prompt.selected_region) return undefined;

  return [
    prompt.selected_region.top_left.x,
    prompt.selected_region.top_left.y,
    prompt.selected_region.bottom_right.x,
    prompt.selected_region.bottom_right.y,
  ];
};

const getElementImageUrl = (element: ElementType): string | undefined => {
  const imageComponent = element.components?.find((component) => component.content_type === 'image');
  return imageComponent?.content || imageComponent?.src_img;
};

export const createToolImagePromptFromElement = (element: ElementType): FormImagePrompt | null => {
  const imageUrl = getElementImageUrl(element);
  if (!element.id || !imageUrl) {
    return null;
  }

  const previewUrl = element.components?.find((c) => c.content_type === 'image')?.src_img || imageUrl;

  return {
    id: element.id,
    status: 'done',
    material: {
      id: element.id,
      version: element.version || '',
      name: element.name,
      share_id: element.share_meta?.status === 'approved' ? element.share_meta?.id : undefined,
    },
    prompt: {
      sourceImage: previewUrl,
      sourceImagePreview: previewUrl,
      selectedImage: imageUrl,
      selectedImagePreview: imageUrl,
    },
  };
};

export const createImagePromptFromTaskPrompt = (prompt: TaskInputPrompts): FormImagePrompt | null => {
  if (prompt.type === 'material' && prompt.material?.id) {
    const content = prompt.content || prompt.material.contents?.[0] || '';
    const imagePrompt: FormImagePrompt = {
      id: prompt.material.id,
      status: 'done',
      material: {
        id: prompt.material.id,
        version: prompt.material.version || '',
        name: prompt.name,
        share_id: prompt.material.share_id,
      },
      prompt: content
        ? {
            sourceImage: content,
            sourceImagePreview: content,
            selectedImage: content,
            selectedImagePreview: content,
          }
        : null,
    };
    return imagePrompt;
  }

  if (prompt.type !== 'image' || !prompt.content) return null;

  const sourceImage = prompt.src_imgs?.[0] || prompt.content;
  const referenceCreationId = prompt.reference?.creation_id;

  return {
    id: referenceCreationId || prompt.content,
    status: 'done',
    prompt: {
      sourceImage,
      sourceImagePreview: sourceImage,
      selectedImage: prompt.content,
      selectedImagePreview: prompt.content,
      selectedImageRange: getSelectedImageRange(prompt),
    },
    reference: referenceCreationId
      ? {
          creation_id: referenceCreationId,
        }
      : undefined,
  };
};

export const createFirstImagePromptFromTask = (task: TaskInfo): FormImagePrompt | null => {
  const prompt = task.input?.prompts?.find((item) => item.type === 'image' || item.type === 'material');
  if (!prompt) return null;

  const imagePrompt = createImagePromptFromTaskPrompt(prompt);
  if (!imagePrompt) return null;

  return {
    ...imagePrompt,
    id: prompt.reference?.creation_id || task.input?.creation_id || prompt.content,
  };
};

export const createVideoPromptFromTask = (task: TaskInfo): ToolTaskFormVideoPrompt | null => {
  const prompt = task.input?.prompts?.find((item) => item.type === 'video');
  if (!prompt?.content) return null;

  const referenceCreationId = prompt.reference?.creation_id || task.input?.creation_id;
  const motionId = prompt.reference?.motion_id;
  const promptResolution = (prompt as TaskInputPrompts & { resolution?: CreationInfo['resolution'] }).resolution;

  const finalReferenceCreationId =
    !!referenceCreationId && referenceCreationId !== '0' ? referenceCreationId : undefined;
  const reference =
    finalReferenceCreationId || motionId
      ? {
          ...(finalReferenceCreationId ? { creation_id: finalReferenceCreationId } : {}),
          ...(motionId ? { motion_id: motionId } : {}),
        }
      : undefined;

  return {
    id: finalReferenceCreationId || motionId || prompt.content,
    status: 'done',
    prompt: {
      video: prompt.content,
      videoPreview: prompt.content,
      duration: typeof task.settings?.duration === 'number' ? task.settings.duration : undefined,
      coverImage: prompt.cover_uri,
      coverImagePreview: prompt.cover_uri,
      resolution: promptResolution,
    } as ToolTaskFormVideoPrompt['prompt'] & {
      resolution?: {
        width: number;
        height: number;
        resolution: string;
      };
    },
    reference,
  };
};

export const createImagePromptFromCreation = (
  creation: CreationInfo | CreationWithTask,
  options?: {
    includeReference?: boolean;
  },
): FormImagePrompt => {
  const previewImageUrl = creation.nomark_uri || creation.uri || '';

  return {
    id: creation.id || '',
    status: 'done',
    prompt: {
      sourceImage: creation.uri || '',
      sourceImagePreview: previewImageUrl,
      selectedImage: creation.uri || '',
      selectedImagePreview: previewImageUrl,
      resolution: creation.resolution,
    } as ImagePrompt & {
      resolution?: {
        width: number;
        height: number;
        resolution: string;
      };
    },
    reference: options?.includeReference
      ? {
          creation_id: creation.id || '',
        }
      : undefined,
  };
};

export const createVideoPromptFromCreation = (
  creation: CreationInfo | CreationWithTask,
  options?: {
    includeReference?: boolean;
  },
): ToolTaskFormVideoPrompt => {
  const previewVideoUrl = getResourceUrl({
    uri: creation.uri || '',
    noMarkUri: creation.nomark_uri || '',
  });

  return {
    id: creation.id || '',
    status: 'done',
    prompt: {
      video: creation.uri || '',
      videoPreview: previewVideoUrl,
      duration: creation.duration,
      coverImage: creation.cover_uri,
      coverImagePreview: creation.cover_uri,
      resolution: creation.resolution,
    } as ToolTaskFormVideoPrompt['prompt'] & {
      resolution?: {
        width: number;
        height: number;
        resolution: string;
      };
    },
    reference: options?.includeReference
      ? {
          creation_id: creation.id || '',
        }
      : undefined,
  };
};

export const isDefined = <T>(value: T | null | undefined): value is T => value !== null && value !== undefined;

export const applyToolFormParamsPatch = (
  updateFormParams: (key: keyof ToolFormParams, params: ToolFormParams[typeof key]) => void,
  patch: Partial<ToolFormParams>,
) => {
  if (patch.imagePrompts !== undefined) {
    updateFormParams('imagePrompts', patch.imagePrompts);
  }
  if (patch.videoPrompts !== undefined) {
    updateFormParams('videoPrompts', patch.videoPrompts);
  }
  if (patch.settings !== undefined) {
    updateFormParams('settings', patch.settings);
  }
  if (patch.creationId !== undefined) {
    updateFormParams('creationId', patch.creationId);
  }
  if (patch.textPrompt !== undefined) {
    updateFormParams('textPrompt', patch.textPrompt);
  }
  if (patch.remixStyle !== undefined) {
    updateFormParams('remixStyle', patch.remixStyle);
  }
  if (patch.audioPrompts !== undefined) {
    updateFormParams('audioPrompts', patch.audioPrompts);
  }
  if (patch.subtitlePrompts !== undefined) {
    updateFormParams('subtitlePrompts', patch.subtitlePrompts);
  }
  if (patch.referenceImagePrompts !== undefined) {
    updateFormParams('referenceImagePrompts', patch.referenceImagePrompts);
  }
  if (patch.oneClickMvVersion !== undefined) {
    updateFormParams('oneClickMvVersion', patch.oneClickMvVersion);
  }
  if (patch.oneClickMvScene !== undefined) {
    updateFormParams('oneClickMvScene', patch.oneClickMvScene);
  }
  if (patch.oneClickMvSubtitleEnabled !== undefined) {
    updateFormParams('oneClickMvSubtitleEnabled', patch.oneClickMvSubtitleEnabled);
  }
  if (patch.oneClickMvSubtitleLanguage !== undefined) {
    updateFormParams('oneClickMvSubtitleLanguage', patch.oneClickMvSubtitleLanguage);
  }
  if ('extraParams' in patch) {
    updateFormParams('extraParams', patch.extraParams);
  }
  if ('offlineAvatar' in patch) {
    updateFormParams('offlineAvatar', patch.offlineAvatar);
  }
};

const buildRecreateExtraParams = (inspirationId?: string, inspirationFrom?: string): ToolFormParams['extraParams'] => {
  if (!inspirationId) return undefined;

  return {
    recreate_source:
      (inspirationFrom as NonNullable<ToolFormParams['extraParams']>['recreate_source']) || 'inspiration',
    recreate_source_id: inspirationId,
  };
};

const getTaskTextPrompt = (task: TaskInfo): string => {
  return task.input?.prompts?.find((prompt) => prompt.type === 'text')?.content || '';
};

export const buildToolTaskRecreatePatch = ({
  tool,
  task,
  baseSettings,
  inspirationId,
  inspirationFrom,
}: {
  tool: ToolTaskType | 'upscale_image';
  task: TaskInfo;
  baseSettings?: TaskInfo['settings'];
  inspirationId?: string;
  inspirationFrom?: string;
}): Promise<Partial<ToolFormParams>> => {
  const firstImagePrompt = createFirstImagePromptFromTask(task);
  const imagePrompts =
    task.input?.prompts?.map((prompt) => createImagePromptFromTaskPrompt(prompt)).filter(isDefined) || [];
  const videoPrompt = createVideoPromptFromTask(task);
  const textPrompt = getTaskTextPrompt(task);
  const remixStylePrompt = task.input?.prompts?.find((prompt) => prompt.type === 'remix_style')?.remix_style;
  const remixStyle = remixStylePrompt?.id
    ? {
        id: remixStylePrompt.id,
        name: remixStylePrompt.name,
        url: remixStylePrompt.url,
      }
    : undefined;

  let patch: Partial<ToolFormParams>;

  switch (tool) {
    case 'upscale_image':
      patch = {
        imagePrompts: firstImagePrompt ? [firstImagePrompt] : [],
        creationId: task.input?.creation_id,
        ...(task.settings?.resolution ? { settings: { resolution: task.settings.resolution } } : {}),
      };
      break;
    case 'upscale':
      patch = {
        videoPrompts: videoPrompt ? [videoPrompt] : [],
        creationId: task.input?.creation_id,
        ...(task.settings?.resolution ? { settings: { resolution: task.settings.resolution } } : {}),
      };
      break;
    case 'face_swap':
      patch = {
        ...(firstImagePrompt ? { imagePrompts: [firstImagePrompt] } : {}),
        ...(videoPrompt ? { videoPrompts: [videoPrompt] } : {}),
        ...(task.settings ? { settings: { ...task.settings } } : {}),
      };
      break;
    case 'remake':
      patch = {
        ...(videoPrompt ? { videoPrompts: [videoPrompt] } : {}),
        imagePrompts,
        textPrompt,
        ...(task.settings ? { settings: { ...(baseSettings || {}), ...task.settings } } : {}),
      };
      break;
    case 'one_click':
      patch = {
        imagePrompts,
        textPrompt,
        ...(task.settings ? { settings: { ...(baseSettings || {}), ...task.settings } } : {}),
      };
      break;
    case 'music_video':
      patch = buildOneClickMvRecreatePatch({ task, baseSettings });
      break;
    case 'style_transfer':
      patch = {
        imagePrompts: firstImagePrompt ? [firstImagePrompt] : [],
        textPrompt,
        ...(remixStyle ? { remixStyle } : {}),
        ...(task.settings ? { settings: { ...(baseSettings || {}), ...task.settings } } : {}),
      };
      break;
    case 'motion_control':
      patch = {
        imagePrompts: firstImagePrompt ? [firstImagePrompt] : [],
        videoPrompts: videoPrompt ? [videoPrompt] : [],
        ...(task.settings ? { settings: { ...(baseSettings || {}), ...task.settings } } : {}),
      };
      break;
    default:
      patch = {};
  }

  const extraParams = buildRecreateExtraParams(inspirationId, inspirationFrom);

  return sanitizeToolRecreatePatchReferences({
    ...patch,
    extraParams,
  });
};

export const buildToolCreationRecreatePatch = ({
  tool,
  creation,
}: {
  tool: 'upscale_image' | 'upscale';
  creation: CreationInfo | CreationWithTask;
}): Partial<ToolFormParams> => {
  if (tool === 'upscale_image') {
    return {
      imagePrompts: [createImagePromptFromCreation(creation)],
      creationId: creation.id,
    };
  }

  return {
    videoPrompts: [createVideoPromptFromCreation(creation)],
    creationId: creation.id,
  };
};

export const createToolImagePromptFromUploadFile = (
  file: ImageUploadFile,
  prompt?: ImagePrompt | null,
): FormImagePrompt => {
  return {
    id: file.id,
    status: file.status === 'done' ? 'done' : 'uploading',
    prompt: prompt ?? file.file,
  };
};

export const createToolAudioPromptFromUploadFile = (file: AudioUploadFile): FormAudioPrompt => {
  return {
    id: file.id,
    status: file.status === 'done' ? 'done' : 'uploading',
    prompt: file.file,
  };
};

export const createToolSubtitlePromptFromUploadFile = (file: SubtitleUploadFile): FormSubtitlePrompt => {
  return {
    id: file.id,
    status: file.status === 'done' ? 'done' : 'uploading',
    prompt: file.file,
  };
};

export const createToolVideoPromptFromUploadFile = (
  file: VideoUploadFile,
  prompt?: VideoPrompt | null,
): ToolTaskFormVideoPrompt => {
  return {
    id: file.id,
    status: file.status === 'done' ? 'done' : 'uploading',
    prompt:
      prompt ??
      (file.file
        ? {
            video: file.file.src,
            videoPreview: file.file.preview,
            duration: file.file.duration,
            coverImage: file.file.cover_url,
            coverImagePreview: file.file.cover_preview_url || file.file.preview,
            width: file.width ?? file.file.width,
            height: file.height ?? file.file.height,
            aspectRatio: file.file.aspectRatio,
            videoId: file.file.id,
            _tempFile: file.file._tempFile,
            _tempCoverImage: file.file._tempCoverImage,
          }
        : null),
  };
};
export type UpscaleSourceResolution = '720p' | '1080p' | '2k' | '4k';
export type UpscaleTargetResolution = '1080p' | '2k' | '4k';

interface ResolutionSizeParams {
  width?: number;
  height?: number;
}

interface ResolutionParams extends ResolutionSizeParams {
  resolution?: string | null;
}

type ResolutionAliasMap = Partial<Record<string, UpscaleSourceResolution>>;

const MAX_4K_PIXELS = 3840 * 2160;
const MAX_4K_LONG_EDGE = 3840;
const MAX_4K_SHORT_EDGE = 2160;

export const UPSCALE_RESOLUTION_PRIORITY = {
  '1080p': 1,
  '2k': 2,
  '4k': 3,
} as const;

const BASE_SOURCE_RESOLUTION_ALIASES: Record<string, UpscaleSourceResolution> = {
  '720p': '720p',
  '1080p': '1080p',
  '2k': '2k',
  '4k': '4k',
};

export const UPSCALE_SOURCE_RESOLUTION_ALIASES: ResolutionAliasMap = {
  '1440p': '1080p',
  '2160p': '2k',
};

function normalizeUpscaleSourceResolution(
  resolution?: string | null,
  aliases?: ResolutionAliasMap,
): UpscaleSourceResolution | null {
  if (!resolution) {
    return null;
  }

  const normalizedResolution = resolution.toLowerCase();
  const resolvedResolution = aliases?.[normalizedResolution] ?? BASE_SOURCE_RESOLUTION_ALIASES[normalizedResolution];

  return resolvedResolution ?? null;
}

function getUpscaleSourceResolutionFromSize(width?: number, height?: number): UpscaleSourceResolution | null {
  if (!width || !height) return null;

  const longEdge = Math.max(width, height);
  const shortEdge = Math.min(width, height);
  const pixels = width * height;

  if (longEdge >= MAX_4K_LONG_EDGE || shortEdge >= MAX_4K_SHORT_EDGE || pixels >= MAX_4K_PIXELS) {
    return '4k';
  }
  if (longEdge >= 2560 || shortEdge >= 1440 || pixels >= 2560 * 1440) {
    return '2k';
  }
  if (longEdge >= 1920 || shortEdge >= 1080 || pixels >= 1920 * 1080) {
    return '1080p';
  }

  return '720p';
}

export function getUpscaleSourceResolution(
  params?: ResolutionParams,
  aliases?: ResolutionAliasMap,
): UpscaleSourceResolution | null {
  const normalizedResolution = normalizeUpscaleSourceResolution(params?.resolution, aliases);
  if (normalizedResolution) {
    return normalizedResolution;
  }

  return getUpscaleSourceResolutionFromSize(params?.width, params?.height);
}

export function getDefaultUpscaleTargetResolution(
  sourceResolution?: UpscaleSourceResolution | null,
): UpscaleTargetResolution {
  switch (sourceResolution) {
    case '720p':
      return '1080p';
    case '1080p':
      return '2k';
    case '2k':
    case '4k':
      return '4k';
    default:
      return '2k';
  }
}

export function isUpscaleTargetResolution(resolution?: string | null): resolution is UpscaleTargetResolution {
  return resolution === '1080p' || resolution === '2k' || resolution === '4k';
}

export function isUpscaleTargetResolutionAvailable(
  resolution?: string | null,
  sourceResolution?: UpscaleSourceResolution | null,
): resolution is UpscaleTargetResolution {
  if (!isUpscaleTargetResolution(resolution)) {
    return false;
  }

  if (!sourceResolution || sourceResolution === '720p') {
    return true;
  }

  return UPSCALE_RESOLUTION_PRIORITY[resolution] > UPSCALE_RESOLUTION_PRIORITY[sourceResolution];
}

export function formatUpscaleResolutionLabel(resolution?: string | null): string | undefined {
  switch (resolution?.toLowerCase()) {
    case '720p':
      return '720P';
    case '1080p':
      return '1080P';
    case '2k':
      return '2K';
    case '4k':
      return '4K';
    default:
      return undefined;
  }
}

export function isResolutionBelow4k({ width, height }: ResolutionSizeParams): boolean {
  if (!width || !height) return true;

  const longEdge = Math.max(width, height);
  const shortEdge = Math.min(width, height);

  return longEdge < MAX_4K_LONG_EDGE && shortEdge < MAX_4K_SHORT_EDGE && width * height < MAX_4K_PIXELS;
}

export function createPromptListHelpers<T extends FormPromptListItem>(
  getPrompts: () => Array<T | null>,
  setPrompts: (prompts: Array<T | null>) => void,
): FormPromptListHelpers<T> {
  return {
    updatePromptItem: (promptId: string, updater: (current: T) => T) => {
      setPrompts(
        getPrompts().map((item) => {
          if (!item || item.id !== promptId || !item.prompt) return item;
          return updater(item);
        }),
      );
    },
    markPromptStatus: (promptId: string, status: 'done' | 'uploading') => {
      setPrompts(
        getPrompts().map((item) => {
          if (!item || item.id !== promptId) return item;
          return { ...item, status };
        }),
      );
    },
  };
}
